/**
 * The channel -> campaign -> ad set -> ad hierarchy generator.
 *
 * The largest block in the demo generator and the one with the most private
 * detail: the industry name tables, the per-stage metric profiles and the tier
 * multipliers all stay module-private, because generateDemoCampaigns is the
 * only name anything outside this file needs.
 */

import { DEMO_CONFIG, PERFORMANCE_TIERS, PLATFORM_MODIFIERS, type PlatformKey } from './demoDataConfig';
import { DEMO_CAMPAIGNS } from './demoDataFixtures';
import { getFunnelStageFromObjective, getFunnelStageFromGenericObjective } from '@/config/funnelConfig';
import { aggregateMetrics } from '@/utils/campaignAggregator';
import type {
  CampaignAdSetWithPerformance, CampaignAdWithPerformance, CampaignWithPerformance,
} from '@/types/campaign';

/**
 * Industry-specific product types for ad sets
 */
const INDUSTRY_PRODUCT_TYPES: Record<string, string[]> = {
  lead_gen: [
    'Enterprise Solutions',
    'Business Services',
    'Professional Services',
    'Consulting Packages',
    'Premium Plans',
    'Service Tiers',
    'Annual Packages',
  ],
  supplement: [
    'Vitamin C Complex',
    'Collagen Peptides',
    'Multivitamin Plus',
    'Immune Booster',
    'Beauty Glow Complex',
    'Omega-3 Fish Oil',
    'Probiotic Formula',
  ],
  ev: [
    'EV Model S Long Range',
    'EV Model X Performance',
    'EV Compact City',
    'EV SUV Family',
    'Charging Package',
    'Premium Interior Upgrade',
  ],
  fmcg: [
    'Snack Bundle Pack',
    'Beverage Multi-Pack',
    'Personal Care Set',
    'Household Essentials',
    'Seasonal Promo Pack',
    'New Variant Launch',
    'Limited Edition Flavor',
  ],
  ecommerce: [
    'New Arrival Collection',
    'Best Seller Bundle',
    'Seasonal Collection',
    'Premium Line',
    'Value Pack',
    'Limited Edition',
  ],
  automotive: [
    'Sedan Premium',
    'SUV Family',
    'Hatchback City',
    'Hybrid Edition',
    'Performance Line',
  ],
}

/**
 * Industry-specific ad set name patterns
 * Balanced across funnel stages: 2 awareness / 3 consideration / 2 conversion
 */
const INDUSTRY_AD_SET_NAMES: Record<string, string[]> = {
  lead_gen: [
    // Awareness (2)
    'Brand Introduction',
    'Product Showcase',

    // Consideration (3)
    'Comparison Shopping',
    'Features Guide',
    'Benefits Explainer',

    // Conversion (2)
    'Lead Submission',
    'Contact Request',
  ],
  supplement: [
    // Awareness (2)
    'Wellness Awareness Campaign',
    'Beauty & Health Launch',

    // Consideration (3)
    'Product Benefits Guide',
    'Ingredient Comparison',
    'Customer Reviews Spotlight',

    // Conversion (2)
    'Flash Sale Promo',
    'Subscribe & Save Offer',
  ],
  ev: [
    // Awareness (2)
    'EV Model Launch Campaign',
    'Sustainable Mobility Showcase',

    // Consideration (3)
    'Range & Specs Comparison',
    'Charging Infrastructure Guide',
    'Tax Benefit Explainer',

    // Conversion (2)
    'Test Drive Booking',
    'Reservation Deposit Campaign',
  ],
  fmcg: [
    // Awareness (2)
    'Brand TVC Campaign',
    'Influencer Collaboration',

    // Consideration (3)
    'Product Review Campaign',
    'Best Seller Spotlight',
    'Promo Highlights',

    // Conversion (2)
    'Flash Sale Conversion',
    'Store Visit Drive',
  ],
  ecommerce: [
    // Awareness (2)
    'New Collection Showcase',
    'Brand Story Campaign',

    // Consideration (3)
    'Best Seller Highlights',
    'Product Comparison Guide',
    'Customer Review Spotlight',

    // Conversion (2)
    'Checkout Conversion Drive',
    'Cart Recovery Campaign',
  ],
  automotive: [
    // Awareness (2)
    'Model Showcase Campaign',
    'Brand Heritage Campaign',

    // Consideration (3)
    'Feature Comparison',
    'Test Drive Guide',
    'Financing Options',

    // Conversion (2)
    'Test Drive Booking',
    'Dealer Visit Drive',
  ],
}

/**
 * Channel-specific ad formats
 */
const CHANNEL_AD_FORMATS: Record<string, string[]> = {
  facebook: ['Video', 'Image', 'Carousel', 'Story', 'Reels'],
  google_display: ['Display Ad', 'Responsive Display', 'Gmail Ad', 'Video'],
  google_search: ['Text Ad', 'Responsive Search Ad', 'Call Ad'],
  performance_max: ['Performance Max', 'P-Max Video', 'P-Max Display'],
  tiktok: ['Video', 'Spark Ad', 'Image'],
  line: ['Banner', 'Rich Message', 'Video'],
}

/**
 * Generate demo ad sets for a specific campaign
 * Renamed from generateDemoAdSets to work with campaign-level hierarchy
 */
function generateDemoAdSetsForCampaign(
  campaignId: string,
  channelId: string,
  campaignObjective?: string,
  classifiedStage?: string,
  performanceTier?: string,
  campaignBudget?: number
): CampaignAdSetWithPerformance[] {
  const numAdSets = Math.floor(randomInRange(2, 5))

  // Performance-weighted budget allocation across ad sets.
  // Weights sum to 1, so ad set budgets sum exactly to campaignBudget,
  // keeping total spend within the campaign cap.
  const adSetWeights: number[] = []
  for (let i = 0; i < numAdSets; i++) {
    adSetWeights.push(0.7 + Math.random() * 0.6)
  }
  const totalAdSetWeight = adSetWeights.reduce((s, w) => s + w, 0)

  const adSets: CampaignAdSetWithPerformance[] = []

  const audiences = ['Broad', 'Lookalike 1%', 'Retargeting', 'Interest-Based']
  const locations = ['Bangkok', 'Chiang Mai', 'Phuket', 'Nationwide']

  // Get industry-specific data (fallback to lead_gen if industry not configured)
  const industry = DEMO_CONFIG.industry
  const productTypes = INDUSTRY_PRODUCT_TYPES[industry] ?? INDUSTRY_PRODUCT_TYPES.lead_gen
  const adSetNamePatterns = INDUSTRY_AD_SET_NAMES[industry] ?? INDUSTRY_AD_SET_NAMES.lead_gen

  // Normalize platform key for ad sets - matches funnelConfig normalization
  const normalizePlatformKey = (key: string): string => {
    const normalized = key.toLowerCase().replace(/[@_\s-]/g, '')
    const aliases: Record<string, string> = {
      // Facebook variants
      fb: 'facebook',
      meta: 'facebook',
      'facebook-ads': 'facebook',
      // Google variants - hyphenated (before underscore removal)
      gdn: 'google',
      'google-ads': 'google',
      'google-search': 'google',
      'google-display': 'google',
      'google-display-network': 'google',
      // Google variants - underscore/after underscore removal
      googledisplay: 'google',
      googlesearch: 'google',
      performancemax: 'google',
      // TikTok variants
      tt: 'tiktok',
      'tiktok-ads': 'tiktok',
      '@tiktok': 'tiktok',
    }
    return aliases[normalized] || normalized
  }

  for (let i = 0; i < numAdSets; i++) {
    const adSetId = `${campaignId}_adset_${i + 1}`

    // Allocate this ad set's share of the campaign budget.
    // Falls back to legacy 50K-150K random budget when no campaign budget
    // is provided (keeps the function usable standalone).
    const adSetBudget = campaignBudget
      ? campaignBudget * (adSetWeights[i] / totalAdSetWeight)
      : Math.floor(50000 + Math.random() * 100000)

    const ads = generateDemoAds(adSetId, channelId, classifiedStage, performanceTier, adSetBudget)

    // Aggregate metrics from ads
    const adsMetrics = ads.map((ad) => ({ metrics: ad }))
    const metrics = aggregateMetrics(adsMetrics)

    adSets.push({
      id: adSetId,
      name: `${productTypes[i % productTypes.length]} - ${adSetNamePatterns[i % adSetNamePatterns.length]}`,
      audience: audiences[i % 4],
      location: locations[i % 4],
      productType: productTypes[i % productTypes.length],
      status: 'active',
      budget: Math.floor(adSetBudget),
      spent: metrics.cost,
      ...metrics,
      objective: campaignObjective, // Inherit from campaign
      platform: normalizePlatformKey(channelId),
      ads,
    })
  }

  return adSets
}

/**
 * Generate demo campaigns for a channel
 * Uses existing DEMO_CAMPAIGNS array with platform filtering
 * Creates hierarchical structure: Channel → Campaigns → AdSets → Ads
 */
export function generateDemoCampaigns(
  channelId: string
): CampaignWithPerformance[] {
  // Filter DEMO_CAMPAIGNS by platform
  const platformCampaigns = DEMO_CAMPAIGNS.filter((c) => c.platform === channelId)

  // Normalize platform key for objective mapping - matches funnelConfig normalization
  const normalizePlatformKey = (key: string): string => {
    const normalized = key.toLowerCase().replace(/[@_\s-]/g, '')
    const aliases: Record<string, string> = {
      // Facebook variants
      fb: 'facebook',
      meta: 'facebook',
      'facebook-ads': 'facebook',
      // Google variants - hyphenated (before underscore removal)
      gdn: 'google',
      'google-ads': 'google',
      'google-search': 'google',
      'google-display': 'google',
      'google-display-network': 'google',
      // Google variants - underscore/after underscore removal
      googledisplay: 'google',
      googlesearch: 'google',
      performancemax: 'google',
      // TikTok variants
      tt: 'tiktok',
      'tiktok-ads': 'tiktok',
      '@tiktok': 'tiktok',
    }
    return aliases[normalized] || normalized
  }

  return platformCampaigns.map((campaign) => {
    // Classify campaign by objective BEFORE generating ad sets so
    // the stage profile can flow down to individual ads.
    let classifiedStage: string | undefined
    if (campaign.objective) {
      const platformKey = normalizePlatformKey(channelId)
      // Try platform-specific objective mapping first
      classifiedStage =
        getFunnelStageFromObjective(campaign.objective, platformKey) ||
        getFunnelStageFromGenericObjective(campaign.objective)
    }

    // Generate ad sets for this campaign — pass stage + tier + budget so
    // ad-level spend stays within the campaign budget cap.
    const adSets = generateDemoAdSetsForCampaign(
      campaign.id,
      channelId,
      campaign.objective,
      classifiedStage,
      campaign.performanceTier,
      campaign.budget
    )

    // Aggregate metrics from ad sets
    const adSetsMetrics = adSets.map((adSet) => ({ metrics: adSet }))
    const metrics = aggregateMetrics(adSetsMetrics)

    return {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      budget: campaign.budget,
      spent: metrics.cost,
      ...metrics,
      objective: campaign.objective,
      platform: normalizePlatformKey(channelId),
      adSets,
      classifiedStage,
    }
  })
}

// ============================================================================
// STAGE-AWARE METRIC PROFILES
// Per-funnel-stage ranges so awareness ≠ consideration ≠ conversion ads.
// ============================================================================

interface StageMetricProfile {
  ctrMin: number       // click-through rate (fraction, e.g. 0.005 = 0.5%)
  ctrMax: number
  leadRateMin: number  // clicks → leads (fraction)
  leadRateMax: number
  convRateMin: number  // leads → conversions (fraction)
  convRateMax: number
  cpcMin: number       // cost-per-click in THB
  cpcMax: number
}

const STAGE_METRIC_PROFILES: Record<string, StageMetricProfile> = {
  aware: {
    ctrMin: 0.005, ctrMax: 0.015,
    leadRateMin: 0.01, leadRateMax: 0.03,
    convRateMin: 0.02, convRateMax: 0.05,
    cpcMin: 8, cpcMax: 15,
  },
  consider: {
    ctrMin: 0.015, ctrMax: 0.035,
    leadRateMin: 0.08, leadRateMax: 0.15,
    convRateMin: 0.10, convRateMax: 0.18,
    cpcMin: 10, cpcMax: 18,
  },
  convert: {
    ctrMin: 0.030, ctrMax: 0.060,
    leadRateMin: 0.20, leadRateMax: 0.35,
    convRateMin: 0.25, convRateMax: 0.40,
    cpcMin: 15, cpcMax: 28,
  },
}

const DEFAULT_STAGE_PROFILE: StageMetricProfile = STAGE_METRIC_PROFILES.consider

/** Performance tier multiplies metric quality (higher tier = better results). */
const PERFORMANCE_TIER_MULTIPLIERS: Record<string, number> = {
  tier1: 1.3,
  tier2: 1.0,
  tier3: 0.8,
  tier4: 0.6,
}

/**
 * Generate demo ads for an ad set
 * Lowest level of hierarchy - generates individual ads with metrics
 */
function generateDemoAds(
  adSetId: string,
  channelId: string,
  classifiedStage?: string,
  performanceTier?: string,
  adSetBudget?: number
): CampaignAdWithPerformance[] {
  const numAds = Math.floor(randomInRange(3, 6))
  const ads: CampaignAdWithPerformance[] = []

  // Get channel-specific ad formats
  const formats = CHANNEL_AD_FORMATS[channelId] || CHANNEL_AD_FORMATS.facebook

  // Get industry-specific product types for ads (fallback to lead_gen)
  const industry = DEMO_CONFIG.industry
  const productTypes = INDUSTRY_PRODUCT_TYPES[industry] ?? INDUSTRY_PRODUCT_TYPES.lead_gen

  // Get platform modifier for channel-specific metrics
  const platformKey = channelId as PlatformKey
  const modifier = PLATFORM_MODIFIERS[platformKey] || {
    roasMultiplier: 1.0,
    ctrMultiplier: 1.0,
    costMultiplier: 1.0,
  }

  // Resolve stage profile and tier multiplier
  const stageKey = classifiedStage ?? 'consider'
  const profile = STAGE_METRIC_PROFILES[stageKey] ?? DEFAULT_STAGE_PROFILE
  const tierMultiplier =
    PERFORMANCE_TIER_MULTIPLIERS[performanceTier ?? 'tier2'] ??
    PERFORMANCE_TIER_MULTIPLIERS.tier2

  // Performance-weighted allocation of the ad set budget across ads.
  // Weights renormalize so per-ad budgets sum exactly to adSetBudget,
  // which keeps ad set and campaign spend within their caps.
  const adWeights: number[] = []
  for (let i = 0; i < numAds; i++) {
    adWeights.push(0.7 + Math.random() * 0.6)
  }
  const totalAdWeight = adWeights.reduce((s, w) => s + w, 0)

  for (let i = 0; i < numAds; i++) {
    // Stage-aware rates with per-ad randomness inside the profile range
    const baseCTR = randomInRange(profile.ctrMin, profile.ctrMax)
    const leadRate = randomInRange(profile.leadRateMin, profile.leadRateMax)
    const convRate = randomInRange(profile.convRateMin, profile.convRateMax)
    const baseCPC = randomInRange(profile.cpcMin, profile.cpcMax)

    // Adjust metrics based on platform modifier and performance tier
    const adjustedCTR = baseCTR * modifier.ctrMultiplier * tierMultiplier
    const adjustedCPC = baseCPC * modifier.costMultiplier
    const adjustedLeadRate = Math.min(leadRate * tierMultiplier, 1)
    const adjustedConvRate = Math.min(convRate * tierMultiplier, 1)

    // Derive impressions from the ad's allocated budget instead of a fixed
    // 50K base:  cost = impressions × CTR × CPC  =>  impressions = budget / (CTR × CPC)
    // This keeps each ad's spend within its share of the ad set budget.
    // Falls back to the legacy 50K random base when no budget is supplied.
    let impressions: number
    if (adSetBudget) {
      const adBudget = adSetBudget * (adWeights[i] / totalAdWeight)
      impressions = Math.floor(adBudget / (adjustedCTR * adjustedCPC))
    } else {
      const variation = 0.7 + Math.random() * 0.6
      impressions = Math.floor(50000 * variation)
    }

    const clicks = Math.floor(impressions * adjustedCTR)
    const leads = Math.floor(clicks * adjustedLeadRate)
    const conversions = Math.floor(leads * adjustedConvRate * modifier.roasMultiplier)
    const cost = clicks * adjustedCPC

    // Calculate ROAS and revenue at ad level
    // Base ROAS from performance tier range, adjusted by platform modifier
    // so ad-level ROAS reconciles with channel-level ROAS
    const tierKey = (performanceTier ?? 'tier2') as keyof typeof PERFORMANCE_TIERS
    const [roasMin, roasMax] = PERFORMANCE_TIERS[tierKey].roasRange
    const baseROAS = randomInRange(roasMin, roasMax)
    const roas = baseROAS * modifier.roasMultiplier
    const revenue = cost * roas

    ads.push({
      id: `${adSetId}_ad_${i + 1}`,
      name: `Ad ${i + 1} - ${formats[i % formats.length]}`,
      format: formats[i % formats.length],
      productType: productTypes[i % productTypes.length],
      status: 'active',
      impressions,
      clicks,
      conversions,
      cost,
      revenue,
      // Cost per CLICK. This divided by CONVERSIONS — the same bug 084bec3
      // fixed in campaignAggregator, in a second place.
      cpc: clicks > 0 ? cost / clicks : 0,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      conversionRate: leads > 0 ? (conversions / leads) * 100 : 0,
      roas,
      adSetId,
      channelId,
    })
  }

  return ads
}

/**
 * Generate random number in range (inclusive)
 */
function randomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
